import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from "../../../styles/gallery.module.css";
import { requesturl } from '../../../shared/utils/requesturl';

const GALLERY_API = `${requesturl}api-gallery.php`;
const MEDIA_API = `${requesturl}media.php?file=`;

const Gallery = ({ isHomepage = false, maxRecords = 3 }) => {
    const [galleries, setGalleries] = useState([]);
    const [displayedTimelineCount, setDisplayedTimelineCount] = useState(isHomepage ? maxRecords : 3);
    const [displayedTimelineImages, setDisplayedTimelineImages] = useState(10);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const searchQuery = (searchParams.get('search') || '').trim();
    const selectedTimeline = (searchParams.get('timeline') || '').trim();

    const timelinesPerLoad = 3;
    const imagesPerTimeline = 3;
    const fullTimelineImagesPerLoad = 10;

    useEffect(() => {
        fetchGalleries();
    }, []);

    useEffect(() => {
        setDisplayedTimelineCount(isHomepage ? maxRecords : timelinesPerLoad);
    }, [isHomepage, maxRecords, searchQuery]);

    useEffect(() => {
        setDisplayedTimelineImages(fullTimelineImagesPerLoad);
    }, [selectedTimeline]);

    const fetchGalleries = async () => {
        try {
            setLoading(true);
            const response = await fetch(GALLERY_API);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const text = await response.text();
            const data = JSON.parse(text);

            if (data.success) {
                setGalleries(data.data || []);
                setError(null);
            } else {
                setError(data.error || 'Failed to fetch galleries');
            }
        } catch (err) {
            setError(err.message);
            console.error('Error fetching galleries:', err);
        } finally {
            setLoading(false);
        }
    };

    const groupedTimelines = useMemo(() => {
        const grouped = {};

        galleries.forEach((item) => {
            const timeline = item.image_timeline || 'Untitled Timeline';
            if (!grouped[timeline]) {
                grouped[timeline] = [];
            }
            grouped[timeline].push(item);
        });

        return grouped;
    }, [galleries]);

    const sortedTimelineEntries = useMemo(() => {
        return Object.entries(groupedTimelines)
            .sort((a, b) => b[0].localeCompare(a[0]));
    }, [groupedTimelines]);

    const filteredTimelineGroups = useMemo(() => {
        if (!searchQuery) {
            return sortedTimelineEntries.map(([timeline, items]) => [
                timeline,
                items.slice(0, imagesPerTimeline)
            ]);
        }

        const normalizedQuery = searchQuery.toLowerCase();

        return sortedTimelineEntries
            .map(([timeline, items]) => {
                const timelineMatches = timeline.toLowerCase().includes(normalizedQuery);

                if (timelineMatches) {
                    return [timeline, items];
                }

                const matchedImages = items.filter((item) =>
                    (item.image_names || '').toLowerCase().includes(normalizedQuery)
                );

                if (matchedImages.length > 0) {
                    return [timeline, matchedImages];
                }

                return null;
            })
            .filter(Boolean);
    }, [imagesPerTimeline, searchQuery, sortedTimelineEntries]);

    const displayedTimelines = useMemo(() => {
        return filteredTimelineGroups.slice(0, displayedTimelineCount);
    }, [filteredTimelineGroups, displayedTimelineCount]);

    const selectedTimelineItems = useMemo(() => {
        if (!selectedTimeline) {
            return [];
        }

        const timelineEntry = sortedTimelineEntries.find(([timeline]) => timeline === selectedTimeline);
        return timelineEntry ? timelineEntry[1] : [];
    }, [selectedTimeline, sortedTimelineEntries]);

    const displayedSelectedTimelineItems = useMemo(() => {
        return selectedTimelineItems.slice(0, displayedTimelineImages);
    }, [displayedTimelineImages, selectedTimelineItems]);

    const handleLoadMoreTimelines = () => {
        setDisplayedTimelineCount((prev) =>
            Math.min(prev + timelinesPerLoad, filteredTimelineGroups.length)
        );
    };

    const handleLoadMoreTimelineImages = () => {
        setDisplayedTimelineImages((prev) =>
            Math.min(prev + fullTimelineImagesPerLoad, selectedTimelineItems.length)
        );
    };

    const handleViewAll = () => {
        navigate('/gallery');
    };

    const openTimeline = (timeline) => {
        const params = new URLSearchParams();
        params.set('timeline', timeline);
        if (searchQuery) {
            params.set('search', searchQuery);
        }
        navigate(`/gallery?${params.toString()}`);
    };

    const closeTimelineView = () => {
        if (searchQuery) {
            navigate(`/gallery?search=${encodeURIComponent(searchQuery)}`);
            return;
        }
        navigate('/gallery');
    };

    if (loading) {
        return (
            <div className={styles.gallerywrapper}>
                <div className={styles.loading}>Loading gallery...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.gallerywrapper}>
                <div className={styles.error}>Error: {error}</div>
            </div>
        );
    }

    if (galleries.length === 0) {
        return (
            <div className={styles.gallerywrapper}>
                <div className={styles.empty}>No gallery items available</div>
            </div>
        );
    }

    if (selectedTimeline) {
        if (selectedTimelineItems.length === 0) {
            return (
                <div className={styles.gallerywrapper}>
                    <button type="button" onClick={closeTimelineView} className={styles.backbtn}>Back to Gallery</button>
                    <div className={styles.empty}>This timeline could not be found.</div>
                </div>
            );
        }

        const hasMoreTimelineImages = displayedTimelineImages < selectedTimelineItems.length;

        return (
            <div className={styles.gallerywrapper}>
                <div className={styles.sectionheader}>
                    <button type="button" onClick={closeTimelineView} className={styles.backbtn}>Back to Gallery</button>
                    <h2 className={styles.heading}>{selectedTimeline}</h2>
                    <p className={styles.pageinfo}>
                        Showing {displayedSelectedTimelineItems.length} of {selectedTimelineItems.length} images
                    </p>
                </div>

                <div className={styles.gallerycontainer}>
                    {displayedSelectedTimelineItems.map((gallery) => (
                        <div key={gallery.image_id} className={styles.gallerycard}>
                            <div className={styles.imagearea}>
                                {gallery.image_path ? (
                                    <img
                                        src={`${MEDIA_API}${encodeURIComponent(gallery.image_path)}`}
                                        alt={gallery.image_names}
                                        className={styles.galleryimage}
                                        onError={(e) => {
                                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="300"%3E%3Crect fill="%23ddd" width="300" height="300"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-size="16"%3EImage Not Available%3C/text%3E%3C/svg%3E';
                                        }}
                                    />
                                ) : (
                                    <div className={styles.noimage}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" viewBox="0 0 16 16">
                                            <path d="M14.5 1a1.5 1.5 0 0 1 1.5 1.5v12a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 0 14.5v-13A1.5 1.5 0 0 1 1.5 0h13zm-13 1h13v12h-13V2zm5 6a1 1 0 1 0-2 0 1 1 0 0 0 2 0z" />
                                        </svg>
                                        <p>No Image</p>
                                    </div>
                                )}
                            </div>
                            <div className={styles.gallerycontent}>
                                <h4 className={styles.gallerytitle}>{gallery.image_names}</h4>
                                <p className={styles.gallerymeta}>
                                    Added: {new Date(gallery.created_at).toLocaleDateString()}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>

                {hasMoreTimelineImages && (
                    <div className={styles.paginationcontainer}>
                        <button type="button" onClick={handleLoadMoreTimelineImages} className={styles.loadmorebtn}>
                            Load More Images ({displayedSelectedTimelineItems.length} of {selectedTimelineItems.length})
                        </button>
                    </div>
                )}
            </div>
        );
    }

    if (filteredTimelineGroups.length === 0) {
        return (
            <div className={styles.gallerywrapper}>
                <div className={styles.empty}>No gallery results matched "{searchQuery}".</div>
            </div>
        );
    }

    const hasMore = displayedTimelineCount < filteredTimelineGroups.length;

    return (
        <div className={styles.gallerywrapper}>
            <div className={styles.sectionheader}>
                <h2 className={styles.heading}>
                    {searchQuery ? `Gallery Search Results for "${searchQuery}"` : 'Gallery'}
                </h2>
            </div>

            <div className={styles.timelinescontainer}>
                {displayedTimelines.map(([timeline, items]) => {
                    const totalItemsInTimeline = groupedTimelines[timeline]?.length || items.length;
                    const canViewAllTimeline = totalItemsInTimeline > items.length;

                    return (
                        <div key={timeline} className={styles.timelinegroup}>
                            <button
                                type="button"
                                className={styles.timelinebutton}
                                onClick={() => openTimeline(timeline)}
                            >
                                {timeline}
                            </button>
                            <div className={styles.gallerycontainer}>
                                {items.map((gallery) => (
                                    <button
                                        key={gallery.image_id}
                                        type="button"
                                        className={styles.gallerycardbutton}
                                        onClick={() => openTimeline(timeline)}
                                    >
                                        <div className={styles.gallerycard}>
                                            <div className={styles.imagearea}>
                                                {gallery.image_path ? (
                                                    <img
                                                        src={`${MEDIA_API}${encodeURIComponent(gallery.image_path)}`}
                                                        alt={gallery.image_names}
                                                        className={styles.galleryimage}
                                                        onError={(e) => {
                                                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="300"%3E%3Crect fill="%23ddd" width="300" height="300"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-size="16"%3EImage Not Available%3C/text%3E%3C/svg%3E';
                                                        }}
                                                    />
                                                ) : (
                                                    <div className={styles.noimage}>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" viewBox="0 0 16 16">
                                                            <path d="M14.5 1a1.5 1.5 0 0 1 1.5 1.5v12a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 0 14.5v-13A1.5 1.5 0 0 1 1.5 0h13zm-13 1h13v12h-13V2zm5 6a1 1 0 1 0-2 0 1 1 0 0 0 2 0z" />
                                                        </svg>
                                                        <p>No Image</p>
                                                    </div>
                                                )}
                                            </div>
                                            <div className={styles.gallerycontent}>
                                                <h4 className={styles.gallerytitle}>{gallery.image_names}</h4>
                                                <p className={styles.gallerymeta}>
                                                    Added: {new Date(gallery.created_at).toLocaleDateString()}
                                                </p>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                            {canViewAllTimeline && (
                                <div className={styles.timelinefooter}>

                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {isHomepage && (
                <div className={styles.sectionfooter}>
                    <button type="button" onClick={handleViewAll} className={styles.viewallbtn}>View All</button>
                </div>
            )}

            {!isHomepage && hasMore && (
                <div className={styles.paginationcontainer}>
                    <button type="button" onClick={handleLoadMoreTimelines} className={styles.loadmorebtn}>
                        Load More Timelines ({displayedTimelines.length} of {filteredTimelineGroups.length})
                    </button>
                </div>
            )}
        </div>
    );
};

export default Gallery;
